import type { ReportLanguage } from '../types';

/**
 * Englische Fassung der Standard-Checkliste (MASTER_CHECKLIST) und ihrer
 * Kategorien — nur fuer Anzeige und Druck.
 *
 * Gespeichert bleibt der deutsche Text: `mergeChecklistEntries` ordnet
 * gespeicherte Punkte ueber Text + Kategorie der Vorlage zu, ein uebersetzter
 * Text wuerde jeden Punkt als eigenen Punkt abspalten. Eigene Punkte und
 * Punkte aus dem KI-Scan stehen nicht in der Tabelle und bleiben, wie sie
 * eingegeben wurden.
 *
 * Englisch nur bei rein englischen Berichten ('en'), wie beim Maengelkatalog
 * (katalogSprache): 'de/en' bleibt deutsch, dort ergaenzt die
 * PDF-Uebersetzung das Englische. Begriffe nach dem Glossar der englischen
 * Katalogfassung (scripts/mangelKatalog/daten/englisch/LEITFADEN.md).
 */
// Als Map, nicht als Objekt: ein eigener Punkt namens „__proto__" oder
// „constructor" faende im Objekt einen geerbten Schluessel und lieferte
// statt Text ein Objekt bzw. eine Funktion.
const PRUEFPUNKTE_EN: ReadonlyMap<string, string> = new Map(Object.entries({
    'Vorankündigung liegt aktuell vor und hängt aus': 'Prior notice is up to date and posted',
    'SiGe-Plan liegt vor und ist aktuell': 'Health and safety plan is available and up to date',
    'Baustellenordnung hängt aus': 'Site rules are posted',
    'Unterweisungsnachweise der Firmen liegen vor': "Contractors' records of safety briefings are available",
    'Gefährdungsbeurteilungen der Firmen liegen vor': "Contractors' risk assessments are available",
    'Ersthelfer-Liste aktuell': 'List of first aiders is up to date',
    'Bauzaun geschlossen und standsicher': 'Site fence closed and stable',
    'Baustellenschilderung / Warnhinweise vorhanden': 'Site signage / warning signs in place',
    'Verkehrswege (Personen/Fahrzeuge) getrennt & frei': 'Traffic routes (pedestrians/vehicles) separated and clear',
    'Erste-Hilfe-Material vorhanden & aufgefüllt': 'First-aid supplies available and replenished',
    'Pausenräume / Sanitäranlagen in Ordnung': 'Break rooms / sanitary facilities in order',
    'Brandschutz (Feuerlöscher) geprüft vorhanden': 'Fire protection (fire extinguishers) in place and inspected',
    'Beleuchtung der Verkehrswege ausreichend': 'Lighting of traffic routes adequate',
    'Persönliche Schutzausrüstung (PSA) wird getragen': 'Personal protective equipment (PPE) is worn',
    'Elektrische Anlagen geprüft (Prüfplaketten)': 'Electrical installations inspected (inspection stickers)',
    'Leitern und Tritte geprüft und mängelfrei': 'Ladders and step stools inspected and free of defects',
    'Umgang mit Gefahrstoffen (Lagerung/Kennzeichnung)': 'Handling of hazardous substances (storage/labelling)',
    'Absturzsicherungen (Seitenschutz) vollständig': 'Fall protection (edge protection) complete',
    'Gerüste: Freigabenschein vorhanden & aktuell': 'Scaffolds: handover certificate in place and up to date',
    'Gerüste: Verankerung & Belag in Ordnung': 'Scaffolds: ties and decking in order',
    'Bodenöffnungen abgedeckt / gesichert': 'Floor openings covered / secured',
    'Kranbetrieb: Standsicherheit & Prüfbescheinigung': 'Crane operation: stability and inspection certificate',
    'Baugrubenböschungen verbaute / geböscht': 'Excavation sides shored / sloped',
    'Arbeitsraumbreite (> 0,50m) eingehalten': 'Working space width (> 0.50 m) maintained',
    'Absturzsicherung an Baugrubenrändern': 'Fall protection at excavation edges',
    'Leitungspläne vorhanden / eingewiesen': 'Utility plans available / briefing given',
    'Zugänge zu Baugruben (Treppentürme/Leitern)': 'Access to excavations (stair towers/ladders)',
}));

const KATEGORIEN_EN: ReadonlyMap<string, string> = new Map(Object.entries({
    'Allgemein': 'General',
    'Dokumente': 'Documents',
    'Baustelleneinrichtung': 'Site set-up',
    'Hochbau': 'Building construction',
    'Tiefbau': 'Civil engineering',
    'Brückenbau': 'Bridge construction',
    'Industrie': 'Industrial',
    'Abbruch': 'Demolition',
    'Benutzerdefiniert': 'Custom',
    'SiGe-Plan': 'Health and safety plan',
}));

/** Text eines Pruefpunkts in der Berichtssprache; Unbekanntes bleibt, wie es ist. */
export function pruefpunktText(text: string, sprache: ReportLanguage | null | undefined): string {
    if (sprache !== 'en') return text;
    return PRUEFPUNKTE_EN.get(text.trim()) ?? text;
}

/** Name einer Checklisten-Kategorie in der Berichtssprache. */
export function kategorieText(kategorie: string, sprache: ReportLanguage | null | undefined): string {
    if (sprache !== 'en') return kategorie;
    return KATEGORIEN_EN.get(kategorie) ?? kategorie;
}
